import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, useTheme } from '@mui/material'
import { Alert, Button, GoogleSearchBar, PlaceDetails, SERVICES_NEEDING_CONSENT, useCookieConsent } from 'ComponentLibrary'
import { getMapsApiKey } from 'ComponentLibrary/src/common/environment'
import { useState } from 'react'
import { AssetLocation } from 'ClientPortal/src/pages/MarketData/types'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type MarketDataSidebarSearchProps = {
    setSelectedLocation: (location: AssetLocation) => void
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
}

type PlaceDataWithLatLng = PlaceDetails & { coords: { latitude: number; longitude: number } }

export const MarketDataSidebarSearch = ({ setSelectedLocation, isOpen, setIsOpen }: MarketDataSidebarSearchProps) => {
    const theme = useTheme()
    const { t } = useTranslation('pages', { keyPrefix: 'marketData.search' })
    const locale = useCurrentResolvedLocale()
    const [placeDetails, setPlaceDetails] = useState<PlaceDataWithLatLng | null>(null)
    const [showError, setShowError] = useState<boolean>(false)

    const setPlaceDetailsCallback = (details: PlaceDetails | null) => {
        if (details?.coords.latitude && details?.coords.longitude) {
            setPlaceDetails(details)
            setShowError(false)
        } else {
            setShowError(true)
        }
    }
    const handleSave = () => {
        if (placeDetails) {
            setSelectedLocation({
                address: {
                    house_number: placeDetails.streetNumber,
                    city: placeDetails.city || '',
                    street: placeDetails.street || '',
                    zip: placeDetails.zipcode || '',
                    country: placeDetails.country || '',
                    state: placeDetails.state || '',
                    coordinates: {
                        latitude: placeDetails.coords.latitude,
                        longitude: placeDetails.coords.longitude,
                    },
                },
            })
            setIsOpen(false)
        }
    }

    const apiKey = getMapsApiKey()
    const { isEnabled } = useCookieConsent(SERVICES_NEEDING_CONSENT.GOOGLE_MAPS)

    return (
        <Dialog open={isOpen}>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogContent>
                <Stack direction="column" gap={theme.spacing(4)} width="516px">
                    <Divider />
                    <Stack direction="column" gap={theme.spacing(6)}>
                        <GoogleSearchBar
                            apiKey={apiKey}
                            setPlaceDetailsCallback={setPlaceDetailsCallback}
                            language={locale}
                            copy={{
                                noMatchingAddress: t('googleSearchBar.noMatchingAddress'),
                                enterAddressToShowOptions: t('googleSearchBar.enterAddressToShowOptions'),
                                consentRequiredLabel: t('googleSearchBar.consentRequiredLabel'),
                                searchLabel: t('googleSearchBar.searchLabel'),
                            }}
                        />
                        {!isEnabled && <div className="google-address-search" />}
                        {showError && (
                            <Alert severity="error" size="small">
                                {t('coordinateError')}
                            </Alert>
                        )}
                    </Stack>
                    <Divider />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setIsOpen(false)} variant="secondary" label={t('cancel')} />
                <Button onClick={handleSave} disabled={!placeDetails} label={t('next')} />
            </DialogActions>
        </Dialog>
    )
}
